import axios from 'axios';
import { BASE_URL } from '../api/config';

// Service du menu « Sociétés » : CRUD de la table societes (API /societes du backend admin).
// Pas de try/catch ici : l'erreur remonte au composant → toast ou alerte de la fenêtre.
// societes.mail = DOMAINE email des comptes de la société (legrand.fr), pas une adresse : ces comptes paient
// toujours par virement (la colonne paiementCB n'est pas gérée). Le backend (societes/schema.js) reste l'autorité :
// il normalise, refuse les messageries grand public et les doublons.

export interface GroupeSociete {
  id: number;
  nom: string;
}

export interface Societe {
  id: number;
  nom: string;
  mail: string;
  // Comptes clients dont l'email est sur le domaine (paient par virement)
  comptes_domaine: number;
  // Rattachements par id_societe : non affichés, ils bloquent seulement la suppression
  comptes_rattaches: number;
  admins_rattaches: number;
  adresses: number;
  groupes: GroupeSociete[];
}

export interface SocieteInput {
  nom: string;
  mail: string;
}

export const LIMITES = { nom: 255, domaine: 100 };

// Même expression que REGEX_DOMAINE de societes/schema.js
export const REGEX_DOMAINE = /^(?=.{3,100}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

// « Contact@Legrand.FR » → « legrand.fr » (comme normaliserDomaine du backend) ; sert aussi à lire la partie
// domaine des anciennes valeurs enregistrées avec une adresse complète
export const normaliserDomaine = (v: string): string => {
  const s = (v || '').trim().toLowerCase();
  return s.slice(s.lastIndexOf('@') + 1).trim();
};

// Contrôles immédiats de la fenêtre ; les messageries grand public ne sont refusées que par le backend
export const erreurDomaine = (mail: string, societes: Societe[], idCourant: number | null): string | null => {
  const d = normaliserDomaine(mail);
  if (!d) return 'Domaine obligatoire (ex. legrand.fr)';
  if (d.length > LIMITES.domaine) return `${LIMITES.domaine} caractères maximum`;
  if (!REGEX_DOMAINE.test(d)) return `« ${d} » n'est pas un domaine email valide (ex. legrand.fr)`;
  const autre = societes.find((s) => s.id !== idCourant && normaliserDomaine(s.mail) === d);
  return autre ? `Domaine déjà utilisé par « ${autre.nom} »` : null;
};

// « 1 groupe », « 2 groupes », « 0 groupe »
export const nombre = (n: number, un: string, plusieurs: string): string => `${n} ${n > 1 ? plusieurs : un}`;

// Liens qui empêchent la suppression (même phrase que resumerLiens du backend), '' s'il n'y en a aucun
export const resumerLiens = (s: Societe): string => {
  const morceaux: string[] = [];
  const ajouter = (n: number, un: string, plusieurs: string) => {
    if (n > 0) morceaux.push(nombre(n, un, plusieurs));
  };
  ajouter(s.comptes_rattaches, 'compte client', 'comptes clients');
  ajouter(s.admins_rattaches, 'utilisateur admin', 'utilisateurs admin');
  ajouter(s.groupes.length, 'groupe', 'groupes');
  ajouter(s.adresses, 'adresse de facturation', 'adresses de facturation');
  if (morceaux.length <= 1) return morceaux.join('');
  return `${morceaux.slice(0, -1).join(', ')} et ${morceaux[morceaux.length - 1]}`;
};

export const messageErreur = (e: unknown, defaut: string): string => {
  if (axios.isAxiosError(e) && e.response && e.response.data) {
    const data = e.response.data as { message?: string; erreurs?: string[] };
    if (Array.isArray(data.erreurs) && data.erreurs.length) return data.erreurs.join(' ');
    if (data.message) return data.message;
  }
  return defaut;
};

export const listerSocietes = async (): Promise<Societe[]> =>
  (await axios.get<Societe[]>(`${BASE_URL}/societes`)).data;

export const creerSociete = async (d: SocieteInput): Promise<Societe> =>
  (await axios.post<Societe>(`${BASE_URL}/societes`, d)).data;

// Modification partielle possible : { nom } seul ne réécrit pas le domaine
export const majSociete = async (id: number, d: Partial<SocieteInput>): Promise<Societe> =>
  (await axios.put<Societe>(`${BASE_URL}/societes/${id}`, d)).data;

export const supprimerSociete = async (id: number): Promise<void> => {
  await axios.delete(`${BASE_URL}/societes/${id}`);
};
